import type { PreRegisterResponse, LatestVisitInfoModel } from "../types/preRegistration";

/**
 * EN-4557: is this check-in a repeat cycle on a multi check-in invitation?
 *
 * All three clauses are required:
 *   - the invitation allows multiple check-ins
 *   - there is no DRAFT visit to PATCH (the first check-in consumed it: that row was flipped to
 *     APPROVED in place, and the read model only ever looks up DRAFT rows)
 *   - the server still offers the previous completed cycle, i.e. we are inside the check-out window
 *
 * The first clause is absent from every ordinary pre-registration response, so this short-circuits
 * before the others are read and nothing about today's behaviour changes for them.
 */
export const isRepeatCheckinCycle = (data?: PreRegisterResponse | null): boolean =>
  data?.allowMultipleCheckins === true &&
  data?.visitInfoModel == null &&
  data?.latestVisitInfoModel != null &&
  !!data.latestVisitInfoModel.id;

/**
 * The answers to reuse, whitelisted.
 *
 * Deliberately omits everything identifying the PREVIOUS visit row (id, visitStatus, check-in and
 * check-out times) and every artifact the server carries forward by itself (visitPhotoURI, signed
 * documents, video). Sending none of those is what triggers carryForwardVisitPhoto,
 * carryForwardDocuments, applyVideoWatched and carryForwardIdentity on the server.
 *
 * fullName and email come from the VISIT row rather than the invitation because
 * ArtifactCarryForwardResolver.isSamePerson compares the name case-exactly, and invitation casing
 * can differ from what was captured at check-in.
 */
export const buildRepeatCheckinVisitorInfo = (
  latest: LatestVisitInfoModel,
  preRegister: PreRegisterResponse,
): Record<string, unknown> => {
  const info: Record<string, unknown> = {
    fullName: latest.fullName ?? preRegister.fullName ?? "",
    email: latest.email ?? preRegister.email ?? "",
    companyName: latest.companyName ?? preRegister.companyName ?? "",
    phoneNumber: latest.phoneNumber ?? preRegister.phoneNumber ?? "",
    hostUserId: latest.hostUserId ?? preRegister.hostUserId ?? "",
    hostName: latest.hostName ?? preRegister.hostName ?? "",
    visitorTypeId: latest.visitorTypeId ?? preRegister.visitorTypeId,
    visitorType: latest.visitorType ?? preRegister.visitorType,
    siteName: preRegister.siteName,
  };
  if (latest.poeId) info.poeId = latest.poeId;
  if (latest.buildingId) info.buildingId = latest.buildingId;
  if (latest.parkingLotId) info.parkingLotId = latest.parkingLotId;
  if (latest.groupName) info.groupName = latest.groupName;
  if (latest.visitCustomFields?.length) {
    // the row id is dropped on purpose: the server mints a fresh UUID for every custom field
    info.visitCustomFieldModels = latest.visitCustomFields.map((f) => ({
      orgCustomFieldId: f.orgCustomFieldId,
      name: f.name,
      value: f.value,
    }));
  }
  Object.keys(info).forEach((k) => {
    if (info[k] === "" || info[k] == null) delete info[k];
  });
  return info;
};
